import { z } from 'zod';
import { FieldAnswer } from './fields.js';
import { Email, IsoDateTime, LocalDate, Phone, TimeOfDay, Uuid, withDefaults } from './common.js';
/**
 * Module `pre-registration` (docs/modules/pre-registration.md). Owners: task S13 (core) and S13b (pre-arrival page
 * API, no-show), which owns the `PreArrival*` shapes below.
 */
export const PreRegistrationStatus = z.enum(['pending_approval', 'expected', 'arrived', 'cancelled', 'no_show', 'rejected']);
export const PreRegistrationSource = z.enum(['portal', 'companion', 'api', 'form', 'import']);
/** Host's answer to a no-show alert. */
export const HostNoShowAnswer = z.enum(['running_late', 'cancelled']);
/** What the visitor has done on the pre-arrival page (shown to the host). */
export const PreArrivalProgress = z.object({
    opened: z.boolean(),
    confirmed: z.boolean(),
    answersDone: z.boolean(),
    /** Notice ids accepted in advance. */
    noticesAccepted: z.array(Uuid),
    noticesRequired: z.number().int(),
    photoDone: z.boolean(),
    carRegistration: z.string().nullable(),
    /** Phase 3 adds documents, induction and permits. */
    lastActivityAt: IsoDateTime.nullable(),
});
export const PreRegistration = z.object({
    id: Uuid,
    siteId: Uuid,
    groupId: Uuid,
    /** Set when the visitor is a member of a repeat group (e.g. an approved contractor). */
    memberId: Uuid.nullable(),
    visitorName: z.string(),
    visitorEmail: z.string().nullable(),
    visitorPhone: z.string().nullable(),
    visitorCompany: z.string().nullable(),
    hostMemberId: Uuid.nullable(),
    hostName: z.string().nullable(),
    /** UTC; shown in the site's zone. */
    expectedAt: IsoDateTime,
    /** Multi-day visits: last moment the QR code and link work. null = the expected day. */
    expectedUntil: IsoDateTime.nullable(),
    notes: z.string(),
    status: PreRegistrationStatus,
    source: PreRegistrationSource,
    visitId: Uuid.nullable(),
    arrivedAt: IsoDateTime.nullable(),
    noShowAlertedAt: IsoDateTime.nullable(),
    hostAnswer: HostNoShowAnswer.nullable(),
    progress: PreArrivalProgress,
    emailStatus: z.enum(['not_sent', 'queued', 'sent', 'failed', 'off']),
    createdByLabel: z.string(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const PreRegistrationCreate = z.object({
    siteId: Uuid,
    groupId: Uuid,
    memberId: Uuid.nullable().default(null),
    visitorName: z.string().trim().min(1).max(120),
    visitorEmail: Email.nullable().default(null),
    visitorPhone: Phone.nullable().default(null),
    visitorCompany: z.string().trim().max(120).nullable().default(null),
    hostMemberId: Uuid.nullable().default(null),
    expectedAt: IsoDateTime,
    expectedUntil: IsoDateTime.nullable().default(null),
    notes: z.string().max(1000).default(''),
    /** Send the expected-visit email now (when the group's email is on and there is an address). */
    sendEmail: z.boolean().default(true),
});
/** Up to 50 visitors for the same visit (one email each). */
export const PreRegistrationBulkCreate = z.object({
    common: PreRegistrationCreate.omit({ visitorName: true, visitorEmail: true, visitorPhone: true, visitorCompany: true, memberId: true }),
    visitors: z
        .array(PreRegistrationCreate.pick({ visitorName: true, visitorEmail: true, visitorPhone: true, visitorCompany: true, memberId: true }))
        .min(1)
        .max(50),
});
/** Only the fields sent are changed (no defaults here: a default would wipe a field the caller left out). */
export const PreRegistrationUpdate = z.object({
    memberId: Uuid.nullable().optional(),
    visitorName: z.string().trim().min(1).max(120).optional(),
    visitorEmail: Email.nullable().optional(),
    visitorPhone: Phone.nullable().optional(),
    visitorCompany: z.string().trim().max(120).nullable().optional(),
    hostMemberId: Uuid.nullable().optional(),
    expectedAt: IsoDateTime.optional(),
    expectedUntil: IsoDateTime.nullable().optional(),
    notes: z.string().max(1000).optional(),
    resendEmail: z.boolean().optional(),
});
export const PreRegistrationListQuery = z.object({
    siteId: Uuid.optional(),
    groupId: Uuid.optional(),
    hostMemberId: Uuid.optional(),
    status: PreRegistrationStatus.optional(),
    /** Site-local dates, inclusive. */
    from: LocalDate.optional(),
    to: LocalDate.optional(),
    search: z.string().trim().max(120).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export const PreRegistrationCancel = z.object({ notify: z.boolean().default(true) });
export const PreRegistrationDecision = z.object({ decision: z.enum(['approve', 'reject']), reason: z.string().max(500).default('') });
export const PreRegistrationHostAnswer = z.object({ answer: HostNoShowAnswer, newExpectedAt: IsoDateTime.nullable().default(null) });
// ---------- Kiosk (device) view ----------
/** GET /pre-registration/device/expected: today's and current multi-day pre-registrations at the device's site. */
export const KioskExpectedVisitor = z.object({
    id: Uuid,
    groupId: Uuid,
    memberId: Uuid.nullable(),
    /** Formatted by the site's privacy setting (exact-match-only sites send searchName only). */
    displayName: z.string(),
    searchName: z.string(),
    hostMemberId: Uuid.nullable(),
    hostName: z.string().nullable(),
    expectedAt: IsoDateTime,
    expectedUntil: IsoDateTime.nullable(),
    status: z.enum(['expected', 'arrived', 'cancelled']),
    /** Steps already done on the pre-arrival page; the kiosk skips them. */
    done: z.object({ questions: z.boolean(), noticeIds: z.array(Uuid), photo: z.boolean() }),
});
export const KioskExpectedList = z.object({ siteId: Uuid, date: LocalDate, visitors: z.array(KioskExpectedVisitor), etag: z.string() });
/** Kiosk config section `pre-registration` (manifest kioskConfig). */
export const PreRegistrationKioskSection = z.object({
    /** base64url key to check CS1.P codes offline (ADR 0015 / qr/index.ts). */
    qrKey: z.string(),
    /** Group ids that need a pre-registration to sign in. */
    requiredGroupIds: z.array(Uuid),
    /** Seconds between refreshes of the expected list. */
    refreshSeconds: z.number().int(),
});
// ---------- Pre-arrival page (public, signed link, task S13b) ----------
export const PreArrivalView = z.object({
    tenantName: z.string(),
    site: z.object({
        name: z.string(),
        address: z.string(),
        directions: z.string(),
        mapUrl: z.string().nullable(),
        parking: z.string(),
        wifi: z.object({ network: z.string(), password: z.string() }).nullable(),
        hostContact: z.object({ name: z.string(), phone: z.string().nullable(), email: z.string().nullable() }).nullable(),
    }),
    visit: z.object({
        visitorName: z.string(),
        expectedAt: IsoDateTime,
        expectedUntil: IsoDateTime.nullable(),
        timeZone: z.string(),
        status: PreRegistrationStatus,
        canReschedule: z.boolean(),
        canCancel: z.boolean(),
    }),
    /** QR to show at the kiosk (also in the email). */
    qrPayload: z.string(),
    /** Sign-in questions of the group (FieldWidget shapes from fields.ts), sensitive ones included (the visitor answers them). */
    questions: z.array(z.unknown()),
    answers: z.array(FieldAnswer),
    notices: z.array(z.object({ id: Uuid, versionId: Uuid, title: z.string(), body: z.string(), needsSignature: z.boolean(), accepted: z.boolean() })),
    photo: z.object({ wanted: z.boolean(), done: z.boolean() }),
    carRegistration: z.object({ wanted: z.boolean(), value: z.string().nullable() }),
    progress: PreArrivalProgress,
});
export const PreArrivalConfirm = z.object({ confirmed: z.literal(true) });
export const PreArrivalReschedule = z.object({ expectedAt: IsoDateTime, message: z.string().max(500).default('') });
export const PreArrivalCancel = z.object({ message: z.string().max(500).default('') });
export const PreArrivalAnswers = z.object({ answers: z.array(FieldAnswer).max(100) });
/** strokes JSON as on the kiosk (ADR 0010) when the notice needs a signature */
export const PreArrivalNoticeAccept = z.object({ versionId: Uuid, signatureStrokes: z.unknown().nullable().default(null) });
export const PreArrivalCar = z.object({ registration: z.string().trim().max(15).nullable() });
// ---------- Settings ----------
const EmailContent = z.object({
    enabled: z.boolean().default(true),
    /** Template with merge fields; empty = i18n default. */
    subject: z.string().max(200).default(''),
    /** Restricted HTML (ADR 0020); empty = i18n default. */
    body: z.string().max(20000).default(''),
    attachmentFileId: Uuid.nullable().default(null),
    replyTo: Email.nullable().default(null),
    includeQr: z.boolean().default(true),
    includeCalendar: z.boolean().default(true),
});
export const PreRegistrationGroupSettings = z.object({
    /** Only pre-registered people can sign in to this group (spec 2.4). */
    required: z.boolean().default(false),
    /** Pre-registrations by hosts (Companion), forms or the API wait for approval (today.approve_preregistrations). */
    requireApproval: z.boolean().default(false),
    expectedEmail: withDefaults(EmailContent),
    prearrival: withDefaults(z.object({
        enabled: z.boolean().default(true),
        askQuestions: z.boolean().default(true),
        acceptNotices: z.boolean().default(true),
        askPhoto: z.boolean().default(false),
        askCarRegistration: z.boolean().default(false),
        /** Label of the group's sign-in question that receives the car registration given on the pre-arrival page (case-insensitive). Empty = not copied. */
        carRegistrationFieldLabel: z.string().trim().max(80).default(''),
        visitorCanReschedule: z.boolean().default(true),
        visitorCanCancel: z.boolean().default(true),
    })),
    noShow: withDefaults(z.object({
        /** Minutes after the expected time before the host is told (0 = off). */
        afterMinutes: z.number().int().min(0).max(1440).default(0),
        channels: z.array(z.enum(['email', 'sms', 'push'])).max(3).default(['email']),
    })),
});
/** Site settings shown on the pre-arrival page (spec 8.6.7: each is a site setting). */
export const PreRegistrationSiteSettings = z.object({
    directions: z.string().max(2000).default(''),
    mapUrl: z.string().max(500).default(''),
    parking: z.string().max(1000).default(''),
    wifiNetwork: z.string().max(64).default(''),
    wifiPassword: z.string().max(64).default(''),
    hostContactName: z.string().max(120).default(''),
    hostContactPhone: z.string().max(32).default(''),
    hostContactEmail: z.string().max(254).default(''),
});
export const PreRegistrationTenantSettings = z.object({
    /** Hours after the visit day that the pre-arrival link still opens. */
    linkGraceHours: z.number().int().min(0).max(168).default(24),
    /** Cancelled, no-show and rejected pre-registrations are deleted after this many days (0 = keep). */
    keepClosedDays: z.number().int().min(0).max(3650).default(90),
    /** Default visit length used for the calendar file when no end is given (minutes). */
    defaultDurationMinutes: z.number().int().min(15).max(1440).default(60),
    /** Kiosk expected-list refresh (seconds). */
    kioskRefreshSeconds: z.number().int().min(30).max(3600).default(120),
    /** Earliest local time the day's no-show close-out runs. */
    closeDayAt: TimeOfDay.default('23:30'),
    /** Most submissions one public form accepts per day; over it the form answers 429 (0 = no limit). */
    formDailyCap: z.number().int().min(0).max(100000).default(200),
    /** Most public-form submissions per email address per day across the business; over it nothing is stored or sent and the person sees the normal thank-you (0 = no limit). */
    formEmailsPerAddressPerDay: z.number().int().min(0).max(1000).default(3),
});
//# sourceMappingURL=preregistrations.js.map